import React, {
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useSelect } from 'downshift';
import classNames from 'classnames';

import { SvgIcon } from 'common/react/components/SvgIcon';
import { CompIcon } from 'common/react/components/CompIcon';
import { TeamIcon } from 'common/react/components/TeamIcon';
import { getTranslation } from 'common/react/utils/translations';
import { getDropdownPosition, getSelectedComp } from './utils';

const DROPDOWN_VERTICAL_OFFSET = 70;

/**
 * @param {object} props - Props.
 * @param {string} props.label - Label.
 * @param {any[]} props.items - Items.
 * @param {Function} [props.itemToString] - Item to string.
 * @param {Function} [props.selectedItemToString] - The selected item's text displayed on the input
 * @param {any} [props.initialSelectedItem] - Initial selected item.
 * @param {any} [props.activeItem] - Active item from state.
 * @param {Function} [props.onSelectedItemChange] - On change callback.
 * @param {boolean} [props.isDisabled] - Is select disabled.
 * @param {boolean} [props.showCompLogoInside] - Displays comp logo alongside option
 * @param {object} [props.defaultItem] - Specify object to display as default item
 * @param {Function} [props.onReset] - Reset button handler
 * @returns {React.Element} Component.
 */
export const SelectDesktopWithDefault = (props) => {
    const selectRef = useRef();
    const toggleButtonRef = useRef();

    const [dropdownPosition, setDropdownPosition] = useState({
        top: 0,
        left: 0,
        width: 0,
        display: 'none'
    });

    const {
        isOpen,
        selectedItem,
        getToggleButtonProps,
        getLabelProps,
        getMenuProps,
        closeMenu,
        highlightedIndex,
        getItemProps
    } = useSelect({
        items: props.items,
        initialSelectedItem: props.initialSelectedItem,
        selectedItem: props.activeItem,
        // @ts-ignore
        onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
            selectRef?.current.dispatchEvent(
                new Event(
                    PULSE.app.common.CONSTANTS.EVENTS.INTERACTION.CHANGE,
                    { bubbles: true }
                )
            );

            if (typeof props.onSelectedItemChange === 'function') {
                props.onSelectedItemChange({
                    selectedItem: newSelectedItem
                });
            }
        },
        stateReducer
    });

    const currentRound =
        typeof props?.currentRoundIndex !== 'undefined'
            ? props.currentRoundIndex
            : false;

    const isDisabled = props?.isDisabled;

    // remove our default item from the list
    const defaultIndex = props.items.indexOf(props.defaultItem);
    if (defaultIndex > -1) {
        props.items.splice(defaultIndex, 1);
    }

    /**
     * @param {any} item - Item.
     * @returns {string} Text.
     */
    const getItemText = (item) =>
        props.itemToString ? props.itemToString(item) : item;

    const getSelectedItemText = (item) => {
        switch (true) {
            case !!props.selectedItemToString:
                return props.selectedItemToString(item);
            case !!props.itemToString:
                return props.itemToString(item);
            default:
                return item.name;
        }
    };

    /**
     * @param {object} state - Current state.
     * @param {object} actionAndChanges - Action and changes object.
     * @returns {object} The new state or changes object.
     */
    function stateReducer(state, actionAndChanges) {
        const { type, changes } = actionAndChanges;
        // this prevents the menu from being closed when the user selects an item with 'Enter' or mouse
        switch (type) {
            case useSelect.stateChangeTypes.ToggleButtonBlur:
                return state;
            default:
                return changes; // otherwise business as usual.
        }
    }

    const onReset = () => {
        toggleButtonRef?.current.focus();
        props.onReset();
    };

    const onElementBlur = (evt) => {
        const element = evt?.relatedTarget || evt?.target;
        if (
            !selectRef.current?.contains(element) &&
            !element.innerHTML.includes(getTranslation('label.clear'))
        ) {
            closeMenu();
        }
    };

    const onClickOutside = (evnt) => {
        if (!selectRef.current?.contains(evnt.target)) {
            closeMenu();
        }
    };

    useEffect(() => {
        document.addEventListener('click', onClickOutside);

        return () => {
            document.removeEventListener('click', onClickOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useLayoutEffect(() => {
        setDropdownPosition(
            getDropdownPosition(isOpen, selectRef, DROPDOWN_VERTICAL_OFFSET)
        );
    }, [isOpen]);

    const selectedComp = useMemo(
        () => getSelectedComp(selectedItem?.providerId, props.showCompLogo),
        [selectedItem, props.showCompLogo]
    );

    const selectHeader =
        isOpen && selectedItem && props.onReset && props.defaultItem ? (
            <div className="select__header">
                {props.title}
                {props.defaultItem.id !== selectedItem.id ? (
                    <button
                        type="button"
                        className="select__header-clear button-new button-new--secondary"
                        tabIndex={0}
                        onClick={onReset}
                        onBlur={onElementBlur}
                    >
                        {getTranslation('label.clear')}
                    </button>
                ) : null}
            </div>
        ) : null;

    const selectItem = (item, index) => {
        const itemText = getItemText(item);
        const isSelected = getItemText(selectedItem) === itemText;
        const compObj = PULSE.app.common.competition.getComp(item?.providerId);

        return (
            <li
                key={`${itemText}${index}`}
                {...getItemProps({ item, index })}
                className={classNames('select__option', {
                    'select__option--selected': isSelected,
                    'select__option--current': currentRound === index,
                    'select__option--highlighted': highlightedIndex === index
                })}
            >
                <div className="select__option-logo-name-wrapper">
                    {props.showTeamLogo && item.providerId && (
                        <>
                            <TeamIcon
                                className="select__option-logo-icon select__option-logo-icon--normal"
                                providerId={item.providerId}
                                theme="notext"
                            />
                            <TeamIcon
                                className="select__option-logo-icon select__option-logo-icon--light"
                                providerId={item.providerId}
                                theme={
                                    PULSE.app.common.team.filterItemTeamTheme[
                                        PULSE.app.common.team.getTeamAbbr(
                                            item.providerId
                                        )
                                    ] ?? 'light'
                                }
                            />
                        </>
                    )}
                    {props.showCompLogo &&
                        props.showCompLogoInside &&
                        selectedItem && (
                            <span
                                className={`select__option-logo-icon--comp competition-nav__comp-icon competition-nav__comp-icon--internal competition-nav__comp-icon--${
                                    compObj?.logoCode?.toLowerCase() ??
                                    compObj?.code?.toLowerCase() ??
                                    item?.logoCode?.toLowerCase() ??
                                    item?.code?.toLowerCase() ??
                                    'afl'
                                }`}
                            >
                                <CompIcon
                                    providerId={
                                        compObj?.providerId ?? item?.providerId
                                    }
                                    className="icon"
                                />
                            </span>
                        )}
                    <span className="item-text">{itemText}</span>
                </div>

                {isSelected && (
                    <SvgIcon
                        icon={`checkmark`}
                        className="select__option-button-icon"
                    />
                )}
            </li>
        );
    };

    const selectDropdown = ReactDOM.createPortal(
        <div
            style={{
                position: 'absolute',
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                zIndex: 80,
                display: dropdownPosition.display
            }}
        >
            <div className="select__options-wrapper">
                {selectHeader}
                <ul
                    {...getMenuProps(
                        {
                            'aria-labelledby': `${PULSE.app.common.idifyString(
                                props.label
                            )}-dropdown-label`
                        },
                        { suppressRefError: true }
                    )}
                    className={classNames('select__options-list', {
                        'u-hide': !isOpen
                    })}
                    onBlur={onElementBlur}
                >
                    {isOpen && props.items?.map(selectItem)}
                </ul>
            </div>
        </div>,
        document.body
    );

    const toggleButtonProps = () => {
        const idifiedLabel = PULSE.app.common.idifyString(props.label);
        return {
            id: `${idifiedLabel}-dropdown-button`,
            'aria-labelledby': !props.hideLabel
                ? `${idifiedLabel}-dropdown-label`
                : false,
            'aria-label': props.hideLabel ? props.label : false,
            ref: toggleButtonRef
        };
    };

    const toggleButtonIcon = () => {
        const defaultIcon = (
            <span className={`competition-nav__icon`}>
                <SvgIcon icon={props.icon} className="icon icon--placeholder" />
            </span>
        );

        if (selectedItem?.default && props.icon) {
            // display default icon if default selected
            return defaultIcon;
        } else if (props.showTeamLogo && selectedItem.teamAbbr) {
            return (
                <TeamIcon
                    className="select__option-logo-icon"
                    providerId={selectedItem.providerId}
                    theme="notext"
                />
            );
        } else if (props.showCompLogo) {
            return (
                <span
                    className={`competition-nav__comp-icon competition-nav__comp-icon--${
                        selectedComp?.logoCode?.toLowerCase() ??
                        selectedComp?.code?.toLowerCase() ??
                        selectedItem?.logoCode?.toLowerCase() ??
                        selectedItem?.code?.toLowerCase() ??
                        'afl'
                    }`}
                >
                    <CompIcon
                        providerId={
                            selectedComp?.providerId ?? selectedItem?.providerId
                        }
                        className="icon"
                    />
                </span>
            );
        } else if (props.icon) {
            return defaultIcon;
        }

        return null;
    };

    return (
        <div
            className={classNames('select', {
                'select--open': isOpen
            })}
            ref={selectRef}
        >
            <button
                type="button"
                {...getToggleButtonProps(toggleButtonProps())}
                className="select__display"
                disabled={isDisabled}
                onBlur={onElementBlur}
            >
                {toggleButtonIcon()}
                <span className="select__content">
                    {props.label && !props.hideLabel ? (
                        <label
                            {...getLabelProps({
                                id: `${PULSE.app.common.idifyString(
                                    props.label
                                )}-dropdown-label`
                            })}
                            className="select__label"
                        >
                            {props.label}
                        </label>
                    ) : null}
                    <span className="select__current">
                        <span className="select__current-text">
                            {getSelectedItemText(selectedItem) || ''}
                        </span>
                        <SvgIcon
                            icon="chevron-down"
                            className={classNames('select__chevron', {
                                'select__chevron--open': isOpen
                            })}
                        />
                    </span>
                </span>
            </button>
            {selectDropdown}
        </div>
    );
};

SelectDesktopWithDefault.propTypes = {
    label: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.any),
    itemToString: PropTypes.func,
    selectedItemToString: PropTypes.func,
    initialSelectedItem: PropTypes.any,
    activeItem: PropTypes.any,
    onSelectedItemChange: PropTypes.func,
    currentRoundIndex: PropTypes.number,
    showCompLogo: PropTypes.bool,
    isDisabled: PropTypes.bool,
    hideLabel: PropTypes.bool,
    showTeamLogo: PropTypes.bool,
    isOpen: PropTypes.bool,
    icon: PropTypes.string,
    title: PropTypes.string,
    showCompLogoInside: PropTypes.bool,
    defaultItem: PropTypes.object,
    onReset: PropTypes.func
};
